import { LOCALES } from '@sjuni/types';
import { cn } from '@sjuni/ui';
import { useTranslation } from 'react-i18next';

import { completenessFor, REQUIRED_LOCALE, type TranslatedValue } from '@/lib/forms/translatable';

/**
 * docs/05-ADMIN-DASHBOARD.md §4.1 — "A per-record translation completeness bar
 * ('EN 60% complete — 3 fields missing') appears in the editor sidebar."
 *
 * Computed from the form's live values rather than the saved record's, so it
 * moves as the editor types. That is the point of putting it beside them: the
 * dashboard chart in §5.1 reports the same figure across the whole site, but a
 * number that only updates on save teaches nobody anything.
 *
 * Georgian is shown too, even though it is required. A required field can still
 * be empty on a draft, and a bar that silently omits the locale that matters
 * most would be the one place this UI hides a gap.
 */
export interface TranslationProgressProps {
  /** Field label keyed by form field name, so the missing list reads. */
  fields: Record<string, { label: string; value: TranslatedValue | null | undefined }>;
  className?: string;
}

export function TranslationProgress({ fields, className }: TranslationProgressProps) {
  const { t } = useTranslation();

  const values = Object.fromEntries(
    Object.entries(fields).map(([name, field]) => [name, field.value]),
  );

  return (
    <section className={cn('rounded-card border border-subtle p-4', className)}>
      <h3 className="text-caption font-extrabold tracking-wide text-muted">
        {t('form.completeness.title')}
      </h3>

      <dl className="mt-3 space-y-3">
        {LOCALES.map((locale) => {
          const { ratio, missing } = completenessFor(values, locale);
          const percent = Math.round(ratio * 100);
          const complete = missing.length === 0;

          return (
            /*
             * A grouping `div` inside a `dl` may contain `dt` and `dd` and
             * nothing else — so the bar and the missing-field line live inside
             * the `dd` rather than beside it. axe reports the other
             * arrangement as `dlitem`, which is the kind of thing that only
             * surfaces once a real screen renders the component.
             */
            <div key={locale}>
              <dt className="text-body-sm font-extrabold uppercase text-primary">{locale}</dt>

              <dd className="mt-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="sr-only">
                    {t('form.completeness.forLocale', { locale: locale.toUpperCase() })}
                  </span>
                  <span className="ms-auto text-caption text-secondary">
                    {t('form.completeness.percent', { percent })}
                  </span>
                </div>

                {/*
                  `progressbar` rather than a bare div: the width is the whole
                  message, and a sighted-only bar tells a screen-reader user
                  nothing. The text beside it repeats the number, so the role
                  carries it and the line below carries the detail.
                */}
                <div
                  role="progressbar"
                  aria-valuenow={percent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={t('form.completeness.forLocale', { locale: locale.toUpperCase() })}
                  className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-alt"
                >
                  <div
                    className={cn(
                      'h-full rounded-full transition-[width] duration-base',
                      complete
                        ? 'bg-success'
                        : locale === REQUIRED_LOCALE
                          ? 'bg-danger'
                          : 'bg-gold-500',
                    )}
                    style={{ width: `${percent}%` }}
                  />
                </div>

                {complete ? null : (
                  <p className="mt-1 text-caption text-muted">
                    {t('form.completeness.missing', {
                      count: missing.length,
                      fields: missing.map((name) => fields[name]?.label ?? name).join(', '),
                    })}
                  </p>
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}
